/**
 * Upgrade funnel tracking — one place for the source labels.
 *
 * Every upgrade prompt has a `source` (e.g. "menu-card", "my-ads-expiry",
 * "modal-limit"). Stages recorded as interaction events (category "profile"):
 *   upgrade-prompt-shown → upgrade-click → pricing-view
 * and then, server-side in the upgrade audit, checkout started / opened / paid
 * (the source rides along to checkout via ?src= and the checkout request).
 * Admin → Plan Upgrade Report shows the funnel per source.
 */
import { trackInteraction } from "@/utils/trackInteraction";

const shownThisPage = new Set();

/** A prompt became visible. Counted once per source per page load. */
export function trackUpgradeShown(source) {
  if (!source || shownThisPage.has(source)) return;
  shownThisPage.add(source);
  trackInteraction("profile", "upgrade-prompt-shown", { label: source, role: "user" });
}

/** The user clicked an upgrade CTA. */
export function trackUpgradeClick(source, plan) {
  trackInteraction("profile", "upgrade-click", { label: source || "direct", role: "user", extra: plan ? { plan } : undefined });
}

/** A prompt was closed without upgrading. */
export function trackUpgradeDismissed(source) {
  trackInteraction("profile", "upgrade-prompt-dismissed", { label: source || "direct", role: "user" });
}

/** "Need more?" custom-plan email link on the Pricing page was clicked. */
export function trackCustomPlanClick(source) {
  trackInteraction("profile", "custom-plan-click", { label: source || "direct" });
}

/** The Pricing page was viewed, attributed to where the user came from. */
export function trackPricingView(source) {
  trackInteraction("profile", "pricing-view", { label: source || "direct" });
}

/** Router location for the Pricing page carrying the source. */
export function pricingRoute(source) {
  return source ? { path: "/pricing", query: { src: source } } : { path: "/pricing" };
}

/** Normalise a source from a URL/query value. */
export function cleanSource(src) {
  const s = String(src || "").trim().toLowerCase().slice(0, 60);
  return /^[a-z0-9][a-z0-9:_-]*$/.test(s) ? s : "";
}
